import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft, Wrench, FileText, AlertTriangle, Pencil, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { AttachmentList } from "../components/site/AttachmentList";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
const STATUS_STYLE = {
  Submitted: "bg-[#54BCE6] text-[#133955]", Assigned: "bg-[#FFE8B0] text-[#7a5b00]",
  "Walkthrough Requested": "bg-[#FFE8B0] text-[#7a5b00]", "Walkthrough Scheduled": "bg-[#C7F0D8] text-[#0f6b3d]",
  Quoted: "bg-[#133955] text-white", "Quote Accepted": "bg-[#C7F0D8] text-[#0f6b3d]", "Quote Declined": "bg-[#FBD5D5] text-[#9b1c1c]",
};
const Badge = ({ s, testid }) => <span data-testid={testid} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLE[s] || "bg-[#EAF7FC] text-[#133955]"}`}>{s}</span>;

// Normalized list of contractors assigned to a repair request (supports legacy singular fields).
const assignedOfRepair = (r) =>
  (r.assigned_contractors?.length
    ? r.assigned_contractors
    : (r.assigned_contractor_id
      ? [{ contractor_id: r.assigned_contractor_id, contractor_ref: r.assigned_contractor_ref, contractor_name: r.assigned_contractor_name }]
      : []));

export default function AdminRepairRequests() {
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    const [rr, ap] = await Promise.all([
      axios.get(`${API}/admin/repair-requests`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/contractors-approved`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setRequests(rr.data.requests || []); setApproved(ap.data.contractors || []);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => { try { await load(); } catch (e) { if (e?.response?.status === 401) { logout(); navigate("/admin/login"); } } finally { setLoading(false); } })();
  }, [token, load, logout, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /admin/repair-requests?repair=<id> opens that request's detail from a notification
  useEffect(() => {
    if (loading) return;
    const repair = searchParams.get("repair");
    if (!repair) return;
    if (requests.some((r) => r.id === repair)) setOpenId(repair);
    else { toast.error("That repair request could not be found."); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="admin-repair-requests">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button data-testid="nav-back-admin" onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC]"><ArrowLeft size={16} /> Project Requests</button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight mb-1">Repair Quote Requests</h1>
        <p className="text-[#173042]/70 mb-6" data-testid="repairs-count">{requests.length} request{requests.length === 1 ? "" : "s"} from agents</p>
        {requests.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)]" data-testid="no-repairs">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Wrench size={26} /></span>
            <p className="text-[#173042] font-semibold">No repair requests yet</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="repairs-list">
            {requests.map((r) => (
              <button key={r.id} onClick={() => setOpenId(r.id)} data-testid={`repair-row-${r.id}`} className="w-full text-left rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-5 hover:border-[#54BCE6] transition-colors">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#54BCE6]">{r.request_ref}</span>
                    <span className="font-bold text-[#133955]">{r.property_address}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.stale && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FBD5D5] text-[#9b1c1c]" data-testid={`repair-stale-${r.id}`}><AlertTriangle size={12} />UPDATE REQUIRED{r.days_since_update != null ? ` · ${r.days_since_update}d` : ""}</span>}
                    {assignedOfRepair(r).length > 0 && (
                      <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`repair-contractor-${r.id}`}>
                        {assignedOfRepair(r).map((e) => (
                          <span key={e.contractor_id} role="button" tabIndex={0} onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/contractors/${e.contractor_id}`); }} className="text-xs font-semibold text-[#0f6b3d] hover:underline cursor-pointer" title="View contractor profile" data-testid={`repair-contractor-${r.id}-${e.contractor_id}`}>{e.contractor_ref}{e.contractor_name ? ` · ${e.contractor_name}` : ""}</span>
                        ))}
                      </span>
                    )}
                    <Badge s={r.status} testid={`repair-status-${r.id}`} />
                  </div>
                </div>
                <p className="text-sm text-[#173042]/70 mt-1">Agent: <span role="button" tabIndex={0} data-testid={`repair-agent-${r.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/admin/agents/${r.agent_id}`); }} className="font-semibold text-[#133955] hover:underline cursor-pointer" title="View agent profile">{r.agent_name}</span>{r.agent_brokerage ? ` (${r.agent_brokerage})` : ""} · {(r.documents || []).length} doc(s)</p>
              </button>
            ))}
          </div>
        )}
      </main>
      <RepairDetail rid={openId} onClose={() => { setOpenId(null); if (searchParams.get("repair")) setSearchParams({}, { replace: true }); }} authHeader={authHeader} token={token} approved={approved} onChange={load} />
    </div>
  );
}

function RepairDetail({ rid, onClose, authHeader, token, approved, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [roleEdit, setRoleEdit] = useState(null); // contractor_id being role-edited
  const [roleDraft, setRoleDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const fileToken = token;

  const load = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/repair-requests/${rid}`, authHeader); setData(data); }
    catch { toast.error("Could not load request."); onClose(); }
    finally { setLoading(false); }
  }, [rid]); // eslint-disable-line

  useEffect(() => { if (rid) load(); }, [rid, load]);

  // Read-only admin view of the agent <-> contractor message thread
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    if (!rid) { setMsgs([]); return; }
    axios.get(`${API}/admin/repair-requests/${rid}/messages`, authHeader)
      .then(({ data }) => setMsgs(data.messages || []))
      .catch(() => {});
  }, [rid]); // eslint-disable-line

  if (!rid) return null;
  const rr = data?.request;

  // Real estate contractors first
  const ranked = [...approved].sort((a, b) => (b.is_real_estate ? 1 : 0) - (a.is_real_estate ? 1 : 0) || (a.contractor_ref || "").localeCompare(b.contractor_ref || ""));

  const assign = async () => {
    if (!sel) { toast.error("Choose a contractor."); return; }
    setBusy(true);
    try { await axios.post(`${API}/admin/repair-requests/${rid}/assign`, { contractor_id: sel, role_label: roleInput.trim() }, authHeader); toast.success("Assigned — contractor notified by email."); setSel(""); setRoleInput(""); await load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign."); } finally { setBusy(false); }
  };

  const saveRole = async (cid) => {
    setBusy(true);
    try { await axios.patch(`${API}/admin/repair-requests/${rid}/assignments/${cid}`, { role_label: roleDraft }, authHeader); setRoleEdit(null); toast.success("Role updated."); await load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role."); } finally { setBusy(false); }
  };

  const unassign = async (cid) => {
    setBusy(true);
    try { await axios.post(`${API}/admin/repair-requests/${rid}/unassign/${cid}`, {}, authHeader); toast.success("Contractor removed from this request."); await load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove contractor."); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-repair-detail">
        {loading || !rr ? (
          <><DialogHeader><DialogTitle className="sr-only">Repair request</DialogTitle><DialogDescription className="sr-only">Loading</DialogDescription></DialogHeader><div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#133955]" size={28} /></div></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[#133955] flex items-center gap-2"><span className="font-mono text-[#54BCE6]">{rr.request_ref}</span> {rr.property_address}</DialogTitle>
              <DialogDescription>Status: <Badge s={rr.status} /></DialogDescription>
            </DialogHeader>

            <div className="rounded-xl bg-[#EAF7FC] p-4 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-[#173042]/50">Agent:</span> <strong className="text-[#133955]">{rr.agent_name}</strong></div>
              <div><span className="text-[#173042]/50">Brokerage:</span> {rr.agent_brokerage || "—"}</div>
              <div className="break-all"><span className="text-[#173042]/50">Email:</span> {rr.agent_email}</div>
              <div><span className="text-[#173042]/50">Phone:</span> {rr.agent_phone || "—"}</div>
              {rr.mls_number && <div><span className="text-[#173042]/50">MLS:</span> {rr.mls_number}</div>}
              {rr.target_close_date && <div><span className="text-[#173042]/50">Close:</span> {rr.target_close_date}</div>}
              <div className="col-span-2 mt-1 whitespace-pre-wrap text-[#173042]/80">{rr.repairs}</div>
            </div>

            {(rr.documents || []).length > 0 && (
              <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Documents & Photos</p>
                <AttachmentList docs={rr.documents} token={fileToken} testid="admin-repair-attachments" />
              </div>
            )}

            <div className="mt-4 rounded-xl bg-[#EAF7FC] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Contractor Assignment</p>
              {assignedOfRepair(rr).length > 0 ? (
                <div className="space-y-2 mb-3" data-testid="repair-assigned-list">
                  {assignedOfRepair(rr).map((e) => (
                    <div key={e.contractor_id} className="flex flex-wrap items-center gap-3" data-testid={`repair-assigned-row-${e.contractor_id}`}>
                      <span className="font-semibold text-[#133955]" data-testid={`repair-assigned-name-${e.contractor_id}`}>
                        <span className="font-mono text-[#54BCE6]">{e.contractor_ref}</span> · {e.contractor_name}
                      </span>
                      {roleEdit === e.contractor_id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <input data-testid={`repair-role-edit-input-${e.contractor_id}`} value={roleDraft} onChange={(ev) => setRoleDraft(ev.target.value)} maxLength={80} placeholder="Role / scope" className="hpc-input bg-white !py-1 !px-2 text-xs w-40" />
                          <button data-testid={`repair-role-save-${e.contractor_id}`} onClick={() => saveRole(e.contractor_id)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#133955] text-white px-3 py-1 text-xs font-bold hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-50"><Check size={12} /> Save</button>
                          <button data-testid={`repair-role-cancel-${e.contractor_id}`} onClick={() => setRoleEdit(null)} className="text-xs font-bold text-[#173042]/60 hover:text-[#9b1c1c]">Cancel</button>
                        </span>
                      ) : (
                        <>
                          {e.role_label && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FFE8B0] text-[#7a5b00]" data-testid={`repair-assigned-role-${e.contractor_id}`}>{e.role_label}</span>}
                          <button data-testid={`repair-role-edit-${e.contractor_id}`} onClick={() => { setRoleEdit(e.contractor_id); setRoleDraft(e.role_label || ""); }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#54BCE6] hover:text-[#133955]" title={e.role_label ? "Edit role" : "Add role"}><Pencil size={11} /> {e.role_label ? "Edit role" : "Add role"}</button>
                        </>
                      )}
                      <button data-testid={`repair-unassign-btn-${e.contractor_id}`} onClick={() => unassign(e.contractor_id)} disabled={busy} className="inline-flex items-center gap-1 rounded-full border-2 border-[#9b1c1c]/30 text-[#9b1c1c] px-3 py-1 text-xs font-bold hover:bg-[#FBD5D5] disabled:opacity-50">Remove</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#173042]/60 mb-3">Not assigned to a contractor yet.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid="repair-assign-select" value={sel} onChange={(e) => setSel(e.target.value)} className="hpc-input bg-white max-w-xs">
                  <option value="">{ranked.length ? "Choose a contractor…" : "No approved contractors"}</option>
                  {ranked.filter((c) => !assignedOfRepair(rr).some((e) => e.contractor_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.is_real_estate ? "★ RE — " : ""}{c.contractor_ref} · {c.dba_name || c.legal_name}</option>)}
                </select>
                <input data-testid="repair-assign-role-input" value={roleInput} onChange={(e) => setRoleInput(e.target.value)} placeholder="Role / scope (optional)" maxLength={80} className="hpc-input bg-white w-44" />
                <button data-testid="repair-assign-btn" onClick={assign} disabled={busy || !sel} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin" />}{assignedOfRepair(rr).length ? "Add contractor" : "Assign"}</button>
              </div>
              <p className="text-xs text-[#173042]/60 mt-2">★ = flagged Real Estate Contractor. You can assign multiple contractors — each is emailed immediately with the agent's info and a portal link. Removing one revokes their access immediately.</p>
            </div>

            {(data.quotes || []).length > 0 && (
              <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Quotes</p>
                {data.quotes.map((q) => <div key={q.id} className="text-sm text-[#173042]/80">{money(q.amount)} — <span className="capitalize">{q.status}</span>{q.scope ? ` · ${q.scope}` : ""}</div>)}
              </div>
            )}
            {rr.walkthrough?.status && rr.walkthrough.status !== "none" && (
              <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Walk-through: {rr.walkthrough.status}{rr.walkthrough.confirmed_datetime ? ` (${rr.walkthrough.confirmed_datetime})` : ""}</p></div>
            )}

            {msgs.length > 0 && (
              <div className="mt-4" data-testid="admin-repair-messages">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Messages <span className="normal-case font-semibold text-[#173042]/50">(read-only · agent ↔ contractor)</span></p>
                <div className="space-y-2 rounded-xl bg-[#EAF7FC] p-3 max-h-64 overflow-y-auto">
                  {msgs.map((m) => (
                    <div key={m.id} className="rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-3 py-2" data-testid={`admin-message-${m.id}`}>
                      <p className="text-[11px] font-bold text-[#54BCE6]">{m.sender_name} <span className="font-semibold text-[#173042]/45 capitalize">({m.sender_role})</span></p>
                      <p className="text-sm text-[#173042] whitespace-pre-wrap break-words">{m.body}</p>
                      <p className="text-[10px] text-[#173042]/45 mt-0.5">{fmt(m.created_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Activity</p>
              <div className="space-y-2" data-testid="repair-activity">{(data.activities || []).map((a) => <div key={a.id} className="flex items-start gap-3 text-sm"><span className="mt-1 h-2 w-2 rounded-full bg-[#54BCE6] shrink-0" /><div><p className="text-[#173042]">{a.description}</p><p className="text-xs text-[#173042]/50">{fmt(a.created_at)} · {a.actor_role}</p></div></div>)}</div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
